import type { FC, ReactNode } from "react"

import type { InstallArea, Panel } from "@logic/lib/design"
import styled from "styled-components"

import type { Estimate } from "~/graphql/client"
import { formatEnergy } from "~/utils/formatEnergy"

const Table = styled.section`
  overflow-x: auto;
  overflow-y: auto;
  max-height: 50vh;
  padding: 0 10px;
  table {
    /* border-collapse: collapse; */
    border-collapse: separate;
    border-spacing: 0px 10px;
    width: 100%;
    min-width: 620px;
  }
  th {
    p {
      font-weight: 500;
      color: var(--queen-blue);
      font-size: var(--step--1);
      text-align: left;
      padding: 0 5px 5px 5px;
    }
  }
  tr {
    &[data-role="area-row"] {
      border-radius: 5px;
      outline: 1px solid var(--graham-grey);
      &:hover {
        outline: 1px solid var(--blue);
      }
    }
    margin-bottom: 50px;
  }
  td {
    cursor: pointer;
    padding: 10px 0;
    /* &:not(:last-child) {
      border-right: 1px solid rgba(0, 0, 0, 0.1);
    } */
    > p {
      font-size: var(--step--1);
      color: black;
      font-weight: 500;
      padding: 0 5px;
      span {
        font-size: var(--step--1);
        color: var(--graham-grey);
        padding-left: 2px;
      }
    }

    > div {
      display: flex;
      align-items: center;
      justify-content: flex-start;
      width: 100%;
      padding: 0 5px;
      p {
        font-size: var(--step--1);
        color: black;
      }
      > span {
        background-color: var(--green-haze);
        display: flex;
        justify-content: center;
        align-items: center;
        padding: 0 8px;
        margin-right: var(--space-3xs);
        border-radius: 20px;
        p {
          color: white;
          transform: translateY(-1px);
        }
      }
    }
  }
`

const COLUMNS = [
  `Area #`,
  `Active Panels`,
  `Dir.`,
  `Azimuth`,
  `Shade`,
  `Tilt`,
  `Array Production`,
  `Energy Per Panel`,
] as const
type Column = (typeof COLUMNS)[number]

type Row = ReturnType<typeof returnRow>

const Columns: Record<Column, (row: Row) => ReactNode> = {
  [`Area #`]: (row: Row) => <p>{row.areaNumber}</p>,
  [`Active Panels`]: (row: Row) => (
    <div>
      <span>
        <p>{row.activeMods.length}</p>
      </span>
      <p>of {row.totalPanels}</p>
    </div>
  ),
  [`Dir.`]: (row: Row) => <p>{row.direction}</p>,
  [`Azimuth`]: (row: Row) => <p>{row.azimuth}</p>,
  [`Shade`]: (row: Row) => <p>{row.shade}%</p>,
  [`Tilt`]: (row: Row) => <p>{row.tilt}</p>,
  [`Array Production`]: (row: Row) => (
    <p>
      {row.arrayProduction}
      <span>kW</span>
    </p>
  ),
  [`Energy Per Panel`]: (row: Row) => (
    <p>
      {row.energyPerPanel}
      <span>kW</span>
    </p>
  ),
}

export const SummaryTable: FC<{
  rows: Row[]
  onClickLegacy?: (id: string) => void
  onClickAdaptive?: (index: number) => void
}> = ({ rows, onClickLegacy, onClickAdaptive }) => {
  const onClick = (id: string, index: number) => {
    if (onClickLegacy) {
      onClickLegacy(id)
    }
    if (onClickAdaptive) {
      onClickAdaptive(index)
    }
  }

  return (
    <Table>
      <table>
        <tr>
          {COLUMNS.map((column, index) => (
            <th key={index}>
              <p>{column}</p>
            </th>
          ))}
        </tr>
        {rows.map((row, index) => (
          <tr
            key={index}
            data-role="area-row"
            onClick={() => onClick(row.id, index)}
          >
            {COLUMNS.map((column, index) => (
              <td key={index}>{Columns[column](row)}</td>
            ))}
          </tr>
        ))}
      </table>
    </Table>
  )
}

export const returnRow = ({
  id,
  mods,
  index,
  area,
  estimates,
  activeMods,
}: {
  id: string
  mods: string[]
  index: number
  area: InstallArea
  estimates: Estimate[]
  activeMods: Panel[]
}): {
  id: string
  areaNumber: number
  activeMods: Panel[]
  totalPanels: number
  direction: string
  azimuth: string | undefined
  shade: number | null
  tilt: number
  arrayProduction: string
  energyPerPanel: string
} => {
  const totalPanels = mods.length
  const direction = azimuthToCardinal(area.azimuth)
  const estimateForInstallArea = estimates.find((e) => e.installAreaId === id)
  const arrayProduction = estimateForInstallArea?.acAnnual ?? NaN
  const energyPerPanel = formatEnergy((arrayProduction ?? 0) / activeMods.length)
  const azimuth = Math.round(area.azimuth ?? 0)
  const tilt = area.slope
  const shade = area.manualShading ?? area.sunroofShading

  return {
    id,
    areaNumber: index + 1,
    activeMods,
    totalPanels,
    direction,
    azimuth: azimuth ? `${azimuth}` : undefined,
    shade,
    tilt,
    arrayProduction: formatEnergy(arrayProduction),
    energyPerPanel,
  }
}

export const azimuthToCardinal = (rawAzimuth: number | null): string => {
  if (rawAzimuth === null) {
    return `N/A`
  }

  const azimuth = (Math.round(rawAzimuth) + 360) % 360

  const cardinalDirections = [`N`, `NE`, `E`, `SE`, `S`, `SW`, `W`, `NW`, `N`]
  const directionRanges = [
    22.5, 67.5, 112.5, 157.5, 202.5, 247.5, 292.5, 337.5, 360,
  ]
  if (Math.abs(azimuth) <= directionRanges[0]) {
    return cardinalDirections[0]
  }
  for (let i = 0; i < directionRanges.length - 1; i++) {
    if (azimuth >= directionRanges[i] && azimuth <= directionRanges[i + 1]) {
      if (
        Math.abs(azimuth - directionRanges[i]) <
        Math.abs(azimuth - directionRanges[i + 1])
      ) {
        return cardinalDirections[i]
      }

      return cardinalDirections[i + 1]
    }
  }

  return `N/A`
}
